import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

// Types
export interface ChapterSegment {
  id: string;
  title: string;
  startTime: number;
  endTime: number;
  startPercentage: number;
  widthPercentage: number;
}

export interface BreakdownChapter {
  timestamp: number;
  chapterTitle: string;
  content: string;
  quotes: Array<{
    timestamp: number;
    text: string;
  }>;
  keyPoints: string[];
}

export interface VideoContentAnalysis {
  videoInfo: {
    title: string;
    description: string;
  };
  tags: string[];
  blurb: string;
  highlightedClips: Array<{
    id: number;
    title: string;
    startTime: number;
    endTime: number;
    description: string;
  }>;
  breakdown: BreakdownChapter[];
  keyQuestions: Array<{
    question: string;
    answer: string;
  }>;
}

interface EpisodeState {
  // Episode Content
  episodeId: string | null;
  content: any;
  cleanReadData: VideoContentAnalysis | null;
  isDataLoading: boolean;
  
  // Player State
  youtubeTime: number;
  youtubeDuration: number;
  isPlaying: boolean;
  playbackSpeed: number;
  ytPlayer: any;
  
  // UI State
  activeTab: string;
  isVideoMinimized: boolean;
  isBottomPlayerExpanded: boolean;
  
  // Chapter State
  chapters: ChapterSegment[];
  currentChapterIndex: number;
  
  // Progress Bar State
  isDragging: boolean;
  dragProgress: number | null;
}

interface EpisodeActions {
  // Content Actions
  setEpisodeId: (id: string) => void;
  setContent: (content: any) => void;
  setCleanReadData: (data: VideoContentAnalysis | null) => void;
  setIsDataLoading: (loading: boolean) => void;
  
  // Player Actions
  setYoutubeTime: (time: number) => void;
  setYoutubeDuration: (duration: number) => void;
  setIsPlaying: (playing: boolean) => void;
  setPlaybackSpeed: (speed: number) => void;
  setYtPlayer: (player: any) => void;
  
  // UI Actions
  setActiveTab: (tab: string) => void;
  setIsVideoMinimized: (minimized: boolean) => void;
  setIsBottomPlayerExpanded: (expanded: boolean) => void;
  
  // Chapter Actions
  setChapters: (chapters: ChapterSegment[]) => void;
  setCurrentChapterIndex: (index: number) => void;
  
  // Progress Actions
  setIsDragging: (dragging: boolean) => void;
  setDragProgress: (progress: number | null) => void;
  
  // Complex Actions
  seekTo: (timestamp: number) => void;
  togglePlayPause: () => void;
  jumpToChapter: (chapterIndex: number) => void;
  updateCurrentChapter: () => void;
  calculateChapters: () => void;
  
  // Reset Actions
  resetPlayer: () => void;
  resetAll: () => void;
}

type EpisodeStore = EpisodeState & EpisodeActions;

// Helper function to calculate chapter segments
const calculateChapterSegments = (
  breakdown: BreakdownChapter[], 
  duration: number
): ChapterSegment[] => {
  if (!breakdown || breakdown.length === 0 || !duration) return [];
  
  return breakdown.map((chapter, index) => {
    const startTime = chapter.timestamp;
    const endTime = index < breakdown.length - 1 
      ? breakdown[index + 1].timestamp 
      : duration;
    
    return {
      id: `chapter-${index}`,
      title: chapter.chapterTitle,
      startTime,
      endTime,
      startPercentage: (startTime / duration) * 100,
      widthPercentage: ((endTime - startTime) / duration) * 100
    };
  });
};

// Helper function to find current chapter index
const findCurrentChapterIndex = (chapters: ChapterSegment[], currentTime: number): number => {
  return chapters.findIndex(chapter => 
    currentTime >= chapter.startTime && currentTime < chapter.endTime
  );
};

export const useEpisodeStore = create<EpisodeStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial State
      episodeId: null,
      content: null,
      cleanReadData: null,
      isDataLoading: false,
      
      youtubeTime: 0,
      youtubeDuration: 0,
      isPlaying: false,
      playbackSpeed: 1,
      ytPlayer: null,
      
      activeTab: 'Transcript',
      isVideoMinimized: false,
      isBottomPlayerExpanded: false,
      
      chapters: [],
      currentChapterIndex: -1,
      
      isDragging: false,
      dragProgress: null,
      
      // Content Actions
      setEpisodeId: (id) => set({ episodeId: id }),
      setContent: (content) => set({ content }),
      setCleanReadData: (data) => set({ cleanReadData: data }),
      setIsDataLoading: (loading) => set({ isDataLoading: loading }),
      
      // Player Actions
      setYoutubeTime: (time) => set({ youtubeTime: time }),
      setYoutubeDuration: (duration) => set({ youtubeDuration: duration }),
      setIsPlaying: (playing) => set({ isPlaying: playing }),
      setPlaybackSpeed: (speed) => {
        const { ytPlayer } = get();
        if (ytPlayer?.setPlaybackRate) {
          ytPlayer.setPlaybackRate(speed);
        }
        set({ playbackSpeed: speed });
      },
      setYtPlayer: (player) => set({ ytPlayer: player }),
      
      // UI Actions
      setActiveTab: (tab) => {
        set({ activeTab: tab });
        
        // Auto-minimize video when Quiz tab is selected
        if (tab === 'Quiz' && !get().isVideoMinimized) {
          set({ isVideoMinimized: true });
        }
      },
      setIsVideoMinimized: (minimized) => set({ isVideoMinimized: minimized }),
      setIsBottomPlayerExpanded: (expanded) => set({ isBottomPlayerExpanded: expanded }),
      
      // Chapter Actions
      setChapters: (chapters) => set({ chapters }),
      setCurrentChapterIndex: (index) => set({ currentChapterIndex: index }),
      
      // Progress Actions
      setIsDragging: (dragging) => set({ isDragging: dragging }),
      setDragProgress: (progress) => set({ dragProgress: progress }),
      
      // Complex Actions
      seekTo: (timestamp) => {
        const { ytPlayer } = get();
        if (ytPlayer?.seekTo) {
          ytPlayer.seekTo(timestamp, true);
          set({ youtubeTime: timestamp });
        }
      },
      
      togglePlayPause: () => {
        const { ytPlayer, isPlaying } = get();
        if (ytPlayer) {
          if (isPlaying) {
            ytPlayer.pauseVideo();
          } else {
            ytPlayer.playVideo();
          }
          set({ isPlaying: !isPlaying });
        }
      },
      
      jumpToChapter: (chapterIndex) => {
        const { chapters, ytPlayer } = get();
        if (chapters[chapterIndex] && ytPlayer?.seekTo) {
          ytPlayer.seekTo(chapters[chapterIndex].startTime, true);
          set({ 
            youtubeTime: chapters[chapterIndex].startTime,
            currentChapterIndex: chapterIndex 
          });
        }
      },
      
      updateCurrentChapter: () => {
        const { chapters, youtubeTime } = get();
        const newIndex = findCurrentChapterIndex(chapters, youtubeTime);
        if (newIndex !== get().currentChapterIndex) {
          set({ currentChapterIndex: newIndex });
        }
      },
      
      calculateChapters: () => {
        const { cleanReadData, youtubeDuration } = get();
        if (cleanReadData?.breakdown && youtubeDuration) {
          const chapters = calculateChapterSegments(cleanReadData.breakdown, youtubeDuration);
          set({ chapters });
        }
      },
      
      // Reset Actions
      resetPlayer: () => set({
        youtubeTime: 0,
        youtubeDuration: 0,
        isPlaying: false,
        playbackSpeed: 1,
        ytPlayer: null,
        isDragging: false,
        dragProgress: null,
        currentChapterIndex: -1,
      }),
      
      resetAll: () => set({
        episodeId: null,
        content: null,
        cleanReadData: null,
        isDataLoading: false,
        youtubeTime: 0,
        youtubeDuration: 0,
        isPlaying: false,
        playbackSpeed: 1,
        ytPlayer: null,
        activeTab: 'Transcript',
        isVideoMinimized: false,
        isBottomPlayerExpanded: false,
        chapters: [],
        currentChapterIndex: -1,
        isDragging: false,
        dragProgress: null,
      }),
    })),
    {
      name: 'episode-store',
    }
  )
);

// Selector hooks for performance optimization
export const useEpisodeContent = () => useEpisodeStore((state) => ({
  episodeId: state.episodeId,
  content: state.content,
  cleanReadData: state.cleanReadData,
  isDataLoading: state.isDataLoading,
}));

export const useEpisodePlayer = () => useEpisodeStore((state) => ({
  youtubeTime: state.youtubeTime,
  youtubeDuration: state.youtubeDuration,
  isPlaying: state.isPlaying,
  playbackSpeed: state.playbackSpeed,
  ytPlayer: state.ytPlayer,
  seekTo: state.seekTo,
  togglePlayPause: state.togglePlayPause,
  setPlaybackSpeed: state.setPlaybackSpeed,
}));

export const useEpisodeChapters = () => useEpisodeStore((state) => ({
  chapters: state.chapters,
  currentChapterIndex: state.currentChapterIndex,
  jumpToChapter: state.jumpToChapter,
  updateCurrentChapter: state.updateCurrentChapter,
}));

export const useEpisodeUI = () => useEpisodeStore((state) => ({
  activeTab: state.activeTab,
  isVideoMinimized: state.isVideoMinimized,
  isBottomPlayerExpanded: state.isBottomPlayerExpanded,
  setActiveTab: state.setActiveTab,
  setIsVideoMinimized: state.setIsVideoMinimized,
  setIsBottomPlayerExpanded: state.setIsBottomPlayerExpanded,
}));

export const useEpisodeProgress = () => useEpisodeStore((state) => ({
  isDragging: state.isDragging,
  dragProgress: state.dragProgress,
  setIsDragging: state.setIsDragging,
  setDragProgress: state.setDragProgress,
}));

// Note: Auto-subscriptions removed to prevent infinite loops
// Use manual calls to updateCurrentChapter() and calculateChapters() as needed
